<template>
    <Loading v-if="loading" />
    <!-- Remote Control View for Mobile / Tablet -->
    <div v-else-if="activeViewMode === 'remote'" class="flex flex-col h-full w-full overflow-hidden p-2.5 sm:p-4 pt-1 sm:pt-3 select-none touch-manipulation bg-slate-100 dark:bg-zinc-950">
        <!-- Top Bar with Room ID, Output Status, Song Switcher, Desktop Mode Toggle -->
        <div class="flex items-center justify-between gap-2 text-gray-700 dark:text-white/80 pb-2.5 pt-0.5 px-0.5 flex-shrink-0 pl-11 sm:pl-1">
            <div class="flex items-center gap-1.5 shrink-0">
                <Tooltip :text="$t('presentation.syncTitle') || 'Presentation Sync & Room PIN'" position="bottom">
                    <button
                        type="button"
                        @click="openSyncModal"
                        class="flex items-center gap-1.5 text-xs font-mono bg-white dark:bg-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-700 text-gray-700 dark:text-zinc-200 px-2.5 py-1.5 rounded-xl border border-gray-300 dark:border-zinc-700 shadow-xs transition-all active:scale-95 cursor-pointer"
                    >
                        <RadioIcon class="w-3.5 h-3.5 text-blue-500 shrink-0" />
                        <span class="font-bold">PIN: {{ roomId }}</span>
                    </button>
                </Tooltip>
                <div 
                    v-if="outputState !== 'live'" 
                    class="text-[10px] sm:text-xs px-2 py-1 rounded-lg font-bold bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300 border border-amber-500/30 uppercase shrink-0"
                    :title="getOutputStateTooltip(outputState)"
                >
                    {{ outputState }}
                </div>
            </div>
            <div class="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
                <select
                    v-if="songsPresentation.length > 1"
                    :value="currentSongIndex"
                    @change="e => onSongChange(Number(e.target.value))"
                    :title="$t('presentation.selectSong') || 'Select Song'"
                    class="bg-white dark:bg-zinc-800 text-xs font-medium text-gray-900 dark:text-white border border-gray-300 dark:border-zinc-700 rounded-xl px-2.5 py-1.5 outline-none max-w-[130px] sm:max-w-[220px] truncate shadow-xs cursor-pointer"
                >
                    <option v-for="(song, sIdx) in songsPresentation" :key="sIdx" :value="sIdx">
                        {{ song.title || `Song ${sIdx + 1}` }}
                    </option>
                </select>
                <Tooltip :text="$t('presentation.desktopMode') || 'Desktop Editor Mode'" position="bottom">
                    <button
                        type="button"
                        @click="toggleViewMode"
                        class="flex items-center gap-1 text-xs bg-white hover:bg-gray-50 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-gray-700 dark:text-zinc-200 border border-gray-300 dark:border-zinc-700 p-1.5 sm:px-2.5 sm:py-1.5 rounded-xl active:scale-95 transition-all shadow-xs cursor-pointer shrink-0"
                    >
                        <ComputerDesktopIcon class="w-4 h-4 text-gray-500 dark:text-zinc-400" />
                        <span class="hidden sm:inline">{{ $t('presentation.desktopMode') }}</span>
                    </button>
                </Tooltip>
            </div>
        </div>

        <!-- Output States Selector (Live, Blank, Freeze, Logo) -->
        <div class="grid grid-cols-4 gap-1.5 sm:gap-2 mb-2.5 flex-shrink-0">
            <Tooltip
                v-for="st in outputStates"
                :key="st.value"
                :text="getOutputStateTooltip(st.value)"
                position="bottom"
                class="w-full"
            >
                <button
                    type="button"
                    @click="setOutputState(st.value)"
                    class="w-full py-2 px-1 rounded-xl text-[11px] sm:text-xs font-bold tracking-wide transition-all active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer text-center shadow-xs"
                    :class="outputState === st.value
                        ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-md ring-2 ring-indigo-500/50'
                        : 'bg-white dark:bg-zinc-900 text-gray-600 dark:text-zinc-300 border border-gray-200 dark:border-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-800'"
                >
                    <span
                        class="w-2 h-2 rounded-full shrink-0"
                        :class="{
                            'bg-emerald-500': st.value === 'live',
                            'bg-gray-400 dark:bg-zinc-500': st.value === 'blank',
                            'bg-cyan-500': st.value === 'frozen',
                            'bg-amber-500': st.value === 'logo'
                        }"
                    ></span>
                    <span class="truncate">{{ $t(st.labelKey) }}</span>
                </button>
            </Tooltip>
        </div>

        <!-- Large Prev / Next Touch Buttons -->
        <div class="grid grid-cols-2 gap-2.5 sm:gap-3 mb-2.5 flex-shrink-0">
            <!-- Prev button -->
            <Tooltip :text="$t('presentation.prevPage') || 'Previous slide (←)'" position="top" class="w-full">
                <button
                    type="button"
                    @click="onRemotePrev"
                    class="w-full h-18 sm:h-22 rounded-2xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-800 dark:text-white flex flex-col items-center justify-center gap-1 hover:bg-gray-50 dark:hover:bg-zinc-800/80 active:bg-gray-100 dark:active:bg-zinc-800 active:scale-95 transition-all shadow-sm dark:shadow-lg cursor-pointer"
                >
                    <ChevronLeftIcon class="w-6 h-6 sm:w-7 sm:h-7 text-gray-600 dark:text-white/80" />
                    <span class="text-xs sm:text-sm font-bold tracking-wide uppercase text-gray-800 dark:text-white/90">{{ $t('presentation.prev') || 'prev' }}</span>
                </button>
            </Tooltip>
            <!-- Next button -->
            <Tooltip :text="$t('presentation.nextPage') || 'Next slide (→)'" position="top" class="w-full">
                <button
                    type="button"
                    @click="onRemoteNext"
                    class="w-full h-18 sm:h-22 rounded-2xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-800 dark:text-white flex flex-col items-center justify-center gap-1 hover:bg-gray-50 dark:hover:bg-zinc-800/80 active:bg-gray-100 dark:active:bg-zinc-800 active:scale-95 transition-all shadow-sm dark:shadow-lg cursor-pointer"
                >
                    <ChevronRightIcon class="w-6 h-6 sm:w-7 sm:h-7 text-gray-600 dark:text-white/80" />
                    <span class="text-xs sm:text-sm font-bold tracking-wide uppercase text-gray-800 dark:text-white/90">{{ $t('presentation.next') || 'next' }}</span>
                </button>
            </Tooltip>
        </div>

        <!-- Bottom Rounded Card with Song Title, Subtitle, and Slide Lyrics List -->
        <div class="bg-white dark:bg-zinc-900 text-gray-900 dark:text-white border border-gray-200/90 dark:border-zinc-800 rounded-3xl p-3.5 sm:p-5 flex flex-col flex-1 min-h-0 shadow-sm dark:shadow-2xl overflow-hidden mb-1">
            <!-- Song Header -->
            <div class="border-b border-gray-100 dark:border-zinc-800 pb-2.5 mb-2 flex-shrink-0 px-1">
                <h2 class="text-xl sm:text-2xl font-black tracking-tight text-gray-900 dark:text-white truncate">
                    {{ songsPresentation[currentSongIndex]?.title || 'Song' }}
                </h2>
                <p class="text-xs font-medium text-gray-500 dark:text-zinc-400 mt-0.5 truncate">
                    {{ songsPresentation[currentSongIndex]?.artist?.name || songsPresentation[currentSongIndex]?.subtitle || $t('presentation.remoteSongSubtitle') }}
                </p>
            </div>

            <!-- Category Jump Chips for Remote Controller (wraps so all categories are visible) -->
            <div v-if="remoteSongCategories.length > 0" class="flex flex-wrap items-center gap-1.5 py-1.5 mb-2 flex-shrink-0 px-0.5 border-b border-gray-100 dark:border-zinc-800">
                <button
                    v-for="cat in remoteSongCategories"
                    :key="cat.id"
                    type="button"
                    @click.stop="onRemoteJumpToCategory(cat.id)"
                    class="px-2.5 py-1 rounded-xl text-xs font-bold tracking-tight border transition-all active:scale-95 shadow-2xs flex items-center gap-1.5 cursor-pointer shrink-0 select-none"
                    :class="pages[currentPageIndex]?.category === cat.id ? cat.activePillClass : cat.pillClass"
                >
                    <span class="w-1.5 h-1.5 rounded-full shrink-0" :class="cat.dotClass"></span>
                    <span>{{ $t(cat.labelKey) || cat.defaultLabel }}</span>
                    <span class="text-[9px] opacity-75 font-mono uppercase">[{{ cat.shortcutKey }}]</span>
                </button>
            </div>

            <!-- Scrollable Slide Pages List -->
            <div class="flex-1 overflow-y-auto space-y-2.5 p-1 sm:p-1.5 pb-6" ref="remoteScrollContainer">
                <div
                    v-for="(page, pIdx) in pages"
                    :key="pIdx"
                    :ref="el => { if (el) remoteSlideRefs[pIdx] = el; }"
                    @click="onRemoteSelectSlide(pIdx, page)"
                    :title="$t('presentation.clickToPresent') || 'Click to project this slide'"
                    :class="[
                        'p-3.5 sm:p-4 rounded-2xl cursor-pointer transition-all duration-150 select-none text-left border relative',
                        pIdx === currentPageIndex
                            ? 'bg-indigo-50/90 dark:bg-zinc-800/90 text-indigo-950 dark:text-white font-semibold border-indigo-300 dark:border-indigo-500/60 shadow-sm ring-1 ring-indigo-400/40 dark:ring-indigo-500/40'
                            : 'bg-gray-50/90 dark:bg-zinc-800/40 hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-700 dark:text-zinc-300 border-gray-200 dark:border-zinc-800/80 shadow-2xs',
                        page.category && getCategoryConfig(page.category)
                            ? `${getCategoryConfig(page.category).leftBorderClass} border-l-[4px]`
                            : 'border-l-slate-300 dark:border-l-zinc-700 border-l-[4px]',
                    ]"
                >
                    <div class="flex items-center justify-between mb-1.5">
                        <div class="flex items-center gap-1.5 flex-wrap">
                            <span class="text-[10px] font-mono font-bold uppercase tracking-wider" :class="pIdx === currentPageIndex ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-zinc-500'">
                                {{ pIdx === 0 && !page.editable ? ($t('presentation.blankTransition') || 'Blank') : `Slide ${pIdx + 1}` }}
                            </span>
                            <span
                                v-if="page.category && getCategoryConfig(page.category)"
                                class="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider border shadow-2xs"
                                :class="getCategoryConfig(page.category)?.chipClass"
                            >
                                <span class="w-1.5 h-1.5 rounded-full shrink-0" :class="getCategoryConfig(page.category)?.dotClass"></span>
                                <span>{{ $t(getCategoryConfig(page.category)?.labelKey) || getCategoryConfig(page.category)?.defaultLabel }}</span>
                            </span>
                        </div>
                        <span v-if="pIdx === currentPageIndex" class="size-2 rounded-full bg-indigo-600 dark:bg-indigo-400 animate-pulse"></span>
                    </div>
                    <div class="whitespace-pre-line text-xs sm:text-sm leading-relaxed break-words font-medium">
                        {{ getSlidePreviewText(page) }}
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-else class="flex flex-col h-[calc(100%-20px)] w-full overflow-hidden">
        <!-- <div v-if="!isPortraitMode" class="fixed top-0 left-0 w-full h-full z-50 bg-black/90 flex items-center justify-center">
            <div class="text-white text-center text-2xl font-bold">Turn the screen to portrait mode to view the presentation.</div>
        </div> -->
        <!-- Toolbar -->
        <div class="flex flex-col md:flex-row items-center justify-between bg-white dark:bg-gray-700 shadow-lg shadow-gray-300 dark:shadow-gray-900 border-b border-gray-200 dark:border-gray-700 rounded-t-lg p-3 flex-shrink-0">
            <div class="flex flex-col md:flex-row gap-2 md:gap-2 justify-start md:justify-between md:items-center w-full md:w-auto z-[35]">
                <!-- <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Select Song:</label> -->
                <SortableSelect
                    v-model="currentSongIndex"
                    :options="songsPresentation"
                    option-label="title"
                    placeholder="Select a song"
                    @change="onSongChange"
                    @reorder="handleSongsReorder"
                />
                <Tooltip :text="$t('presentation.addNewSong') || 'Add new song'" position="bottom" class="z-[30]">
                    <div class="hidden lg:flex items-center justify-center size-8 rounded-full text-green-500 cursor-pointer hover:text-green-600 hover:bg-green-100 p-1 z-[30]" 
                        @click="createNewSong">
                        <PlusIcon class="w-4 h-4" />
                    </div>
                </Tooltip>
                <Tooltip :text="$t('presentation.removeSong') || 'Remove song'" position="bottom" class="z-[30]">
                    <div class="hidden lg:flex items-center justify-center size-8 rounded-full text-red-500 cursor-pointer hover:text-red-600 hover:bg-red-100 p-1 z-[30]" 
                        @click="removeSong">
                        <TrashIcon class="w-4 h-4" />
                    </div>
                </Tooltip>
                <div class="flex flex-row items-center gap-2">
                    <Tooltip :text="$t('presentation.syncTitle') || 'Presentation Sync & WebRTC/Ably Connection'" position="bottom" class="z-[30]">
                        <button 
                            type="button"
                            @click="openSyncModal" 
                            class="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/50 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg text-xs font-bold transition-all active:scale-95 shadow-xs cursor-pointer"
                        >
                            <RadioIcon class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            <span>Room PIN: {{ roomId }}</span>
                        </button>
                    </Tooltip>

                    <!-- Local Network Status / Toggle Button -->
                    <Tooltip :text="isWebRTCActive ? ($t('presentation.webrtcActiveTooltip') || 'Rede Local Conectada. Clique para desconectar.') : (isWebRTCStarting ? ($t('presentation.localNetworkConnectingTooltip') || 'Conectando à Rede Local...') : ($t('presentation.webrtcInactiveTooltip') || 'Rede Local Desconectada. Clique para conectar.'))" position="bottom" class="z-[30]">
                        <button
                            type="button"
                            @click="toggleWebRTC"
                            :disabled="isWebRTCStarting"
                            class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 shadow-xs cursor-pointer border"
                            :class="[
                                isWebRTCActive 
                                    ? 'bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' 
                                    : (isWebRTCStarting 
                                        ? 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800 animate-pulse' 
                                        : 'bg-gray-50 hover:bg-gray-100 dark:bg-slate-800/60 dark:hover:bg-slate-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700')
                            ]"
                        >
                            <span class="relative flex h-2 w-2">
                                <span v-if="isWebRTCActive" class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span class="relative inline-flex rounded-full h-2 w-2" :class="isWebRTCActive ? 'bg-emerald-500' : (isWebRTCStarting ? 'bg-amber-500' : 'bg-gray-400')"></span>
                            </span>
                            <span class="whitespace-nowrap">
                                {{ isWebRTCStarting ? ($t('presentation.localNetworkConnecting') || 'Conectando...') : (isWebRTCActive ? ($t('presentation.localNetworkOnline') || 'Rede Local') : ($t('presentation.localNetworkOffline') || 'Rede Local Off')) }}
                            </span>
                            <span v-if="isWebRTCActive && webrtcPeerCount > 0" class="ml-0.5 px-1.5 py-0.5 bg-emerald-200/70 dark:bg-emerald-800/80 text-emerald-800 dark:text-emerald-200 rounded-full text-[10px] font-extrabold leading-none">
                                {{ webrtcPeerCount }}
                            </span>
                        </button>
                    </Tooltip>
                </div>
            </div>
            
            <div class="flex flex-row items-center gap-3 relative justify-end">
                <Tooltip :text="$t('presentation.syncTitle') || 'Presentation Sync & Local Network'" position="left" class="z-[30]">
                    <div class="flex items-center justify-center size-8 rounded-full text-blue-500 cursor-pointer hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-950 p-1" 
                        @click="openSyncModal">
                        <RadioIcon class="w-8 h-8" />
                    </div>
                </Tooltip>
                <Tooltip :text="$t('presentation.remoteMode') || 'Remote Control Mode'" position="left" class="z-[30]">
                    <div class="flex items-center justify-center size-8 rounded-full text-blue-500 cursor-pointer hover:text-blue-600 hover:bg-blue-100 p-1" 
                        @click="toggleViewMode">
                        <DevicePhoneMobileIcon class="w-8 h-8" />
                    </div>
                </Tooltip>
                <Tooltip :text="$t('presentation.openPresentationView') || 'Open presentation display (projector/TV)'" position="left" class="z-[30]">
                    <div class="flex items-center justify-center size-8 rounded-full text-blue-500 cursor-pointer hover:text-blue-600 hover:bg-blue-100 p-1" 
                        :disabled="saving"
                        @click="openPresentationView">
                        <PresentationChartBarIcon class="w-8 h-8" />
                    </div>
                </Tooltip>
                <Tooltip :text="$t('presentation.savePresentation') || 'Save presentation'" position="left" class="z-[30]">
                    <div class="flex items-center justify-center size-8 rounded-full text-blue-500 cursor-pointer hover:text-blue-600 hover:bg-blue-100 p-1" 
                        :disabled="saving"
                        @click="savePresentation">
                        <CloudArrowUpIcon class="w-8 h-8" :class="{'animate-pulse': saving}" />
                    </div>
                </Tooltip>
                <Tooltip :text="$t('presentation.resetPresentation') || 'Recreate slides'" position="left" class="z-[30]">
                    <div class="flex items-center justify-center size-8 rounded-full text-blue-500 cursor-pointer hover:text-blue-600 hover:bg-blue-100 p-1"
                        @click="resetPresentation">
                        <ArrowPathIcon class="w-8 h-8" :class="{'animate-spin': resetting}" />
                    </div>
                </Tooltip>
                <Tooltip :text="$t('presentation.uploadImages') || 'Upload Images'" position="left" class="z-[30]">
                    <div class="flex items-center justify-center size-8 rounded-full text-blue-500 cursor-pointer hover:text-blue-600 hover:bg-blue-100 p-1" 
                        @click="openImageUploadPanel">
                        <InboxArrowDownIcon class="w-8 h-8" :class="{'animate-pulse': uploading}" />
                    </div>
                </Tooltip>
            </div>
        </div>
        <!-- Main Content -->
        <div class="flex flex-row space-between flex-1 w-full border border-gray-200 overflow-hidden min-h-0">
            <PresentationPages ref="presentationPagesRef"
                :data="songsPresentation[currentSongIndex]"
                :songBackground="currentSongVideo?.videoId || currentSongBackground"
                :songBackgroundAdjust="presentationGroup.presentations[currentSongIndex]?.backgroundAdjust ?? null"
                :title="songsPresentation[currentSongIndex]?.title || ''"
                :pages="pages"
                :screen="docV2?.configuration?.screen || '4:3'"
                :currentPageIndex="currentPageIndex"
                @select="setCurrentPageObject"
                @back-to-previous="backToPrevious"
                @go-to-next="goToNext"
                @reorder="handlePageReorder"
                @onNewPage="handleNewPage"
                @onRemovePage="handleRemovePage"
                @onDuplicatePage="handleDuplicatePage"
                @onToggleSkip="handleToggleSkipPage"
                @update:category="handlePageCategoryUpdate"
                @update:title="handleTitleUpdate" />
            <div class="relative hidden md:flex flex-col flex-1 overflow-hidden p-4 justify-center items-center bg-gray-100 pl-8 pr-8 min-w-0">
                <div class="w-full flex justify-center pb-2">
                    <!-- Sempre reserva a altura (blank desabilitada): palco não muda de tamanho/posição -->
                    <PresentationToolbar
                        :editor="currentPageObject?.editable ? activeEditor : null"
                        @align="handleAlignLayer"
                        @textAlign="handleTextAlign"
                    />
                </div>
                <div v-if="!currentPageObject?.editable" class="absolute top-2 left-2 z-[36] max-w-[70%] truncate flex flex-row items-center gap-2 rounded-full bg-gray-100/95 dark:bg-gray-800/95 border border-gray-200 dark:border-gray-700 px-2.5 py-1 text-gray-500 dark:text-gray-400 text-xs shadow-sm">
                    <LockClosedIcon class="size-4 shrink-0" />
                    <span class="truncate">{{ $t('presentation.blankLocked') }}</span>
                </div>
                <div ref="containerRef"
                    class="presentation-container relative flex flex-col justify-center items-center bg-black rounded-lg shadow-lg overflow-hidden shrink-0"
                    :style="containerStyle"
                    @pointerdown.self="clearLayerSelection"
                >
                    <!-- Stage Logo Overlay in Editor -->
                    <div v-if="outputState === 'logo'" class="absolute inset-0 z-[40] flex items-center justify-center p-8 bg-black pointer-events-none select-none">
                        <img
                            :src="docV2?.configuration?.logoUrl || '/triad-logo.png'"
                            class="max-w-[50%] max-h-[50%] object-contain"
                            alt="Logo"
                        />
                    </div>
                    <!-- Stage Blank Overlay in Editor -->
                    <div v-else-if="outputState === 'blank'" class="absolute inset-0 z-[40] flex items-center justify-center bg-black pointer-events-none select-none">
                        <span class="text-xs font-mono text-gray-500 uppercase tracking-widest">{{ $t('presentation.outputBlank') }}</span>
                    </div>

                    <BackgroundLayer :src="stageBgSrc" :adjust="stageBgAdjust" />
                    <div class="flex flex-col w-full h-full z-[35] page-container-editor" :class="[textClass]">
                        <!-- z-order = ordem do array (painel reorder reflete aqui) -->
                        <template v-for="layer in liveLayersVisible" :key="layer.id">
                            <div
                                v-if="isImageLayer(layer)"
                                class="absolute"
                                :data-layer-id="layer.id"
                                :style="{
                                    left: `${boxOf(layer).x}%`,
                                    top: `${boxOf(layer).y}%`,
                                    width: `${boxOf(layer).w}%`,
                                }"
                                :class="{ 'ring-2 ring-indigo-500 rounded': isLayerSelected(layer.id) }"
                                @pointerdown="imagePointerDown($event, layer)"
                            >
                                <img
                                    :src="layer.src"
                                    class="w-full h-auto block select-none"
                                    :style="imageLayerStyle(layer)"
                                    draggable="false"
                                    alt=""
                                />
                                <div
                                    v-if="isLayerSelected(layer.id) && !layer.locked"
                                    class="absolute -bottom-2 -right-2 z-40 w-5 h-5 rounded-full bg-white border-2 border-indigo-500 cursor-nwse-resize shadow"
                                    @pointerdown.stop="boxResizeStart($event, layer)"
                                ></div>
                            </div>
                            <div
                                v-else-if="layer.type === 'video'"
                                class="absolute aspect-video"
                                :data-layer-id="layer.id"
                                :style="{
                                    left: `${boxOf(layer).x}%`,
                                    top: `${boxOf(layer).y}%`,
                                    width: `${boxOf(layer).w}%`,
                                    ...layerStyle(layer),
                                }"
                                :class="{
                                    'ring-2 ring-indigo-500 rounded': isLayerSelected(layer.id),
                                    'border border-dashed border-slate-500/60 rounded bg-slate-800/40 flex items-center justify-center': !layer.videoId,
                                }"
                                @pointerdown="imagePointerDown($event, layer)"
                            >
                                <template v-if="layer.videoId">
                                    <iframe
                                        :key="`${layer.id}-${layer.videoId}-${layer.start}-${layer.end}-${layer.loop}`"
                                        :src="videoEmbedUrl(layer, { autoplay: 0, controls: 1 })"
                                        class="w-full h-full rounded border-0 pointer-events-none"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowfullscreen
                                    ></iframe>
                                    <div
                                        class="absolute inset-0 z-10"
                                        @pointerdown.stop="imagePointerDown($event, layer)"
                                    ></div>
                                </template>
                                <template v-else>
                                    <div class="flex flex-col items-center justify-center gap-1.5 p-4 text-center select-none pointer-events-none">
                                        <PlayCircleIcon class="w-8 h-8 text-slate-400" />
                                        <span class="text-xs font-medium text-slate-300">{{ layer.name || 'Vídeo' }}</span>
                                        <span class="text-[11px] text-slate-400">{{ $t('presentation.videoPlaceholder') || 'Cole o link no painel lateral' }}</span>
                                    </div>
                                </template>
                                <div
                                    v-if="isLayerSelected(layer.id) && !layer.locked"
                                    class="absolute -top-3 -left-3 z-40 w-6 h-6 rounded-full bg-slate-800 text-white flex items-center justify-center cursor-grab active:cursor-grabbing shadow"
                                    @pointerdown.stop="boxMoveStart($event, layer)"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h.008v.008H8.25V6.75zm0 3h.008v.008H8.25V9.75zm0 3h.008v.008H8.25v-.008zm0 3h.008v.008H8.25v-.008zm3-9h.008v.008H11.25V6.75zm0 3h.008v.008H11.25V9.75zm0 3h.008v.008H11.25v-.008zm0 3h.008v.008H11.25v-.008zm3-9h.008v.008H14.25V6.75zm0 3h.008v.008H14.25V9.75zm0 3h.008v.008H14.25v-.008zm0 3h.008v.008H14.25v-.008z" />
                                    </svg>
                                </div>
                                <div
                                    v-if="isLayerSelected(layer.id) && !layer.locked"
                                    class="absolute -bottom-2 -right-2 z-40 w-5 h-5 rounded-full bg-white border-2 border-indigo-500 cursor-nwse-resize shadow"
                                    @pointerdown.stop="boxResizeStart($event, layer)"
                                ></div>
                            </div>
                            <div
                                v-else
                                class="absolute"
                                :data-layer-id="layer.id"
                                :style="{
                                    left: `${boxOf(layer).x}%`,
                                    top: `${boxOf(layer).y}%`,
                                    width: `${boxOf(layer).w}%`,
                                    ...boxTextStyle(layer),
                                }"
                                :class="{
                                    'ring-2 ring-indigo-500 rounded': isLayerSelected(layer.id),
                                    'min-h-[3rem] border border-dashed border-slate-500/60 rounded': isEmptyLayer(layer),
                                }"
                                @pointerdown.self="toggleLayerSelect(layer.id, $event)"
                            >
                                <span
                                    v-if="isEmptyLayer(layer)"
                                    class="absolute inset-0 flex items-center justify-center text-xs font-semibold text-slate-400 pointer-events-none select-none"
                                >
                                    {{ layer.name || '' }}
                                </span>
                                <div
                                    v-if="isLayerSelected(layer.id) && !layer.locked"
                                    class="absolute -top-3 -left-3 z-40 w-6 h-6 rounded-full bg-slate-800 text-white flex items-center justify-center cursor-grab active:cursor-grabbing shadow"
                                    @pointerdown.stop="boxMoveStart($event, layer)"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h.008v.008H8.25V6.75zm0 3h.008v.008H8.25V9.75zm0 3h.008v.008H8.25v-.008zm0 3h.008v.008H8.25v-.008zm3-9h.008v.008H11.25V6.75zm0 3h.008v.008H11.25V9.75zm0 3h.008v.008H11.25v-.008zm0 3h.008v.008H11.25v-.008zm3-9h.008v.008H14.25V6.75zm0 3h.008v.008H14.25V9.75zm0 3h.008v.008H14.25v-.008zm0 3h.008v.008H14.25v-.008z" />
                                    </svg>
                                </div>
                                <div
                                    v-if="isLayerSelected(layer.id) && !layer.locked"
                                    class="absolute -bottom-2 -right-2 z-40 w-5 h-5 rounded-full bg-white border-2 border-indigo-500 cursor-nwse-resize shadow"
                                    @pointerdown.stop="boxResizeStart($event, layer)"
                                ></div>
                                <TipTapEditor
                                    :editable="!!currentPageObject?.editable"
                                    :content="layer.html || ''"
                                    @update:content="(html) => onLayerInput(layer, html)"
                                    @focus="onLayerFocus(layer, $event)"
                                    toolbar="none"
                                    class="presentation-editor"
                                />
                            </div>
                        </template>
                    </div>
                </div>
                <div class="flex items-center gap-3 pt-4 z-[30]">
                    <Tooltip :text="$t('presentation.prevPage')" position="top">
                        <div class="p-4 bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer" >
                            <ArrowLeftIcon class="w-6 h-6" 
                            @click="handleKeyDown({key: 'ArrowLeft', preventDefault: () => {}, stopPropagation: () => {}})"/>
                        </div>
                    </Tooltip>
                    <span class="text-sm font-mono font-semibold text-gray-500 dark:text-gray-400 min-w-[64px] text-center select-none">
                        {{ pages.length > 0 ? `${currentPageIndex + 1} / ${pages.length}` : '–' }}
                    </span>
                    <Tooltip :text="$t('presentation.nextPage')" position="top">
                        <div class="p-4 bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer" >
                            <ArrowRightIcon class="w-6 h-6" 
                                @click="handleKeyDown({key: 'ArrowRight', preventDefault: () => {}, stopPropagation: () => {}})" />
                        </div>
                    </Tooltip>
                </div>
                <!-- Estado da saída: live | blank | frozen | logo -->
                <div class="flex items-center justify-center gap-2 pt-3 z-[30]">
                    <Tooltip
                        v-for="st in outputStates"
                        :key="st.value"
                        :text="getOutputStateTooltip(st.value)"
                        position="top"
                    >
                        <button
                            type="button"
                            @click="setOutputState(st.value)"
                            class="px-3 py-1.5 rounded-lg border text-xs font-bold transition-colors cursor-pointer"
                            :class="outputState === st.value
                                ? 'bg-slate-800 text-white border-slate-800 dark:bg-white dark:text-slate-900 dark:border-white shadow'
                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'"
                        >
                            {{ $t(st.labelKey) }}
                        </button>
                    </Tooltip>
                </div>
            </div>

        <!-- Layers sidebar (direita, dentro do flex principal) -->
        <div class="hidden md:flex flex-col w-[270px] shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 overflow-hidden">
            <LayersPanel
                :layers="currentPageLayers"
                :selected-id="selectedLayerId"
                :selected-ids="selectedLayerIds"
                :screen="docV2?.configuration?.screen || '4:3'"
                :lines-per-slide="String(docV2?.configuration?.linesPerSlide || groupSize)"
                :lines-options="linesOptions"
                :selected-template="selectedTemplate"
                :template-options="templateOptions"
                :template-target="templateTarget"
                :template-target-options="templateTargetOptions"
                :logo-url="docV2?.configuration?.logoUrl || null"
                :effects="docV2?.configuration?.effects !== false"
                :effect-type="docV2?.configuration?.effectType || docV2?.configuration?.transition || 'fade'"
                :effect-duration="docV2?.configuration?.effectDuration ?? 500"
                :page-effect="currentPageObject?.effect !== false"
                :page-editable="!!currentPageObject?.editable"
                :song-video="currentSongVideo"
                @update:screen="selectScreen"
                @update:lines-per-slide="selectLinesPerSlide"
                @update:template="selectTemplate"
                @update:template-target="selectTemplateTarget"
                @update:effects="handleUpdateEffects"
                @update:effect-type="handleUpdateEffectType"
                @update:effect-duration="handleUpdateEffectDuration"
                @open-logo-upload="openLogoUpload"
                @remove-logo="removeLogo"
                @toggle-page-effect="togglePageEffect"
                @update:song-video="handleUpdateSongVideo"
                @select-layer="toggleLayerSelect"
                @toggle-visibility="handleToggleLayerVisibility"
                @toggle-lock="handleToggleLayerLock"
                @delete-layer="handleDeleteLayer"
                @rename-layer="handleRenameLayer"
                @reorder-layers="handleReorderLayers"
                @add-text="handleAddTextBox"
                @add-image="requestImageLayer"
                @add-video="handleAddVideoLayer"
                @replicate-layers="handleReplicateLayers"
                @update-layer-style="handleUpdateLayerStyle"
            />
        </div>
        </div>
    </div>

    <!-- Image Upload Side Panel -->
    <SidePanel 
        ref="imageUploadPanel" 
        :headline="$t('presentation.uploadImages') || 'Upload Images'" 
        :subtitle="$t('presentation.uploadImagesSideSubtitle') || 'Manage assets for slides and logo'"
        widthClass="sm:w-[420px]"
        @onclose="onImageUploadPanelClose" 
        :showBackdrop="false"
    >
        <TriadFileInput 
            :id="route.params.id" 
            direction="gallery"
            @selected="handleImageSelected" 
        />
    </SidePanel>
    <SongBackgroundModal ref="songBackgroundModal" :playlistId="route.params.id" @select="onSongBackgroundSelected" />
    <PresentationSyncModal 
        ref="presentationSyncModal" 
        :roomId="roomId" 
        :playlistId="route.params.id" 
        @update:roomId="onRoomIdUpdated" 
        @room-changed="onRoomIdUpdated" 
    />
</template>
<script setup>
import { onMounted, onUnmounted, ref, watch, nextTick, computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import appStore from '../store/app.store'
import PresentationPages from '@/components/presentation/PresentationPages.vue';
import PresentationToolbar from '@/components/presentation/PresentationToolbar.vue';
import LayersPanel from '@/components/presentation/LayersPanel.vue';
import BackgroundLayer from '@/components/presentation/BackgroundLayer.vue';
import TipTapEditor from '@/components/common/elements/TipTapEditor.vue';
import SortableSelect from '@/components/common/elements/SortableSelect.vue';
import TriadFileInput from '@/components/form/TriadFileInput.vue';
import { Presentation, PresentationGroup } from '@/models/presentation';
import { LockClosedIcon } from '@heroicons/vue/24/solid';
import {realtimeService} from '@/service/realtime.service'
import Message from '@/models/message'
import toastStore from '@/store/toast.store'
import { PlusIcon, CloudArrowUpIcon, ArrowLeftIcon,
        ArrowRightIcon, InboxArrowDownIcon,
        TrashIcon, PlayCircleIcon,
        ArrowPathIcon,
        PresentationChartBarIcon, ChevronLeftIcon,
        ChevronRightIcon, DevicePhoneMobileIcon,
        ComputerDesktopIcon, RadioIcon } from '@heroicons/vue/24/solid';
import dialogStore from '@/store/dialog.store';
import presentationService from '@/service/presentation.service';
import playlistService from '@/service/playlist.service';
import { normalizeToV2, denormalizeToV1, groupToV2, regroupPages, pageToV1, stripInlineColors } from '@/utils/presentationConverter';
import { normalizeLayers, synthesizeLayersFromLegacy, newSlideLayers, createTextBox, createImageLayer, createVideoLayer, layerBox, ensurePageLayers, imageLayerStyle, layerStyle, videoEmbedUrl } from '@/utils/presentationLayers';
import { BUILT_IN_TEMPLATES, TEMPLATE_TARGETS, getTemplate } from '@/utils/presentationTemplates';
import SongBackgroundModal from '@/components/presentation/SongBackgroundModal.vue';
import PresentationSyncModal from '@/components/presentation/PresentationSyncModal.vue';
import { getThumbnailUrl, isYoutubeUrl, getVideoId, getYouTubeUrl } from '@/utils/youtube';
import { getCategoryConfig, getCategoryByShortcut, findNextSlideByCategory, getSongCategories, detectSlideCategory, extractSongSectionsFromLyric, matchSlideCategoryFromSongSections } from '@/utils/slideCategories';
import { openPresentationWindow, initScreenDetails } from '@/utils/presentationUrl';


const route = useRoute();
const { t } = useI18n();
const songsPresentation = ref([])
const loading = ref(true);
const currentSongIndex = ref(0)
const presentationPagesRef = ref(null)
const pages = ref([])
const currentPageObject = ref(null);
const currentPageIndex = ref(0);
const remoteSongCategories = computed(() => getSongCategories(pages.value));

const onRemoteJumpToCategory = (categoryId) => {
    const nextIdx = findNextSlideByCategory(pages.value, currentPageIndex.value, categoryId);
    if (nextIdx !== null) {
        onRemoteSelectSlide(nextIdx, pages.value[nextIdx]);
    }
};
// Editor TipTap com foco (shared toolbar comanda este)
const activeEditor = ref(null);
const groupSize = ref(2);
const presentationGroup = ref(new PresentationGroup());
// Doc v2 normalizado (padrão persistido). Guarda configuration/name/roomId
// entre loads e saves; nulo até o primeiro load.
const docV2 = ref(null);
// loadData ainda não concluiu: saves prematuros postariam doc vazio e
// apagariam a apresentação no servidor.
const presentationLoaded = ref(false);

const outputStates = [
    { value: 'live', labelKey: 'presentation.outputLive' },
    { value: 'blank', labelKey: 'presentation.outputBlank' },
    { value: 'frozen', labelKey: 'presentation.outputFreeze' },
    { value: 'logo', labelKey: 'presentation.outputLogo' },
];
const outputState = ref('live');

const getOutputStateTooltip = (st) => {
    switch (st) {
        case 'live':
            return t('presentation.outputLiveDesc') || 'Broadcast current slide live to viewer';
        case 'blank':
            return t('presentation.outputBlankDesc') || 'Blackout screen on viewer';
        case 'frozen':
            return t('presentation.outputFreezeDesc') || 'Freeze output on current slide while navigating';
        case 'logo':
            return t('presentation.outputLogoDesc') || 'Display logo on viewer';
        default:
            return '';
    }
};

// Device detection & Remote Control Mode
const isMobileDevice = ref(false);
const userForcedView = ref(null); // 'remote' | 'desktop' | null
const remoteSlideRefs = ref([]);
const remoteScrollContainer = ref(null);

const checkDevice = () => {
    if (typeof window === 'undefined') return;
    const isSmallScreen = window.innerWidth < 1024;
    const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
    isMobileDevice.value = isSmallScreen || isTouch;
};

const activeViewMode = computed(() => {
    if (userForcedView.value) return userForcedView.value;
    if (route.query?.mode === 'remote') {
        return 'remote';
    }
    return isMobileDevice.value ? 'remote' : 'desktop';
});

const toggleViewMode = () => {
    if (activeViewMode.value === 'remote') {
        userForcedView.value = 'desktop';
    } else {
        userForcedView.value = 'remote';
    }
};

const getSlidePreviewText = (page) => {
    if (!page) return '';
    if (page.editable === false || page.name === 'Blank — transição' || page.name === 'Blank') {
        return t('presentation.blankTransition') || 'Blank — transição';
    }
    if (page.content) {
        const text = String(page.content).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        if (text) return text;
    }
    if (Array.isArray(page.layers) && page.layers.length > 0) {
        const textLayers = page.layers.filter(l => l && l.type !== 'image' && l.type !== 'video' && l.visible !== false);
        const combined = textLayers.map(l => String(l.html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()).filter(Boolean).join('\n');
        if (combined) return combined;
    }
    return page.name || page.title || '';
};

const clientId = ref('ctrl_' + Math.random().toString(36).slice(2, 9));

const onRemotePrev = () => {
    handlePrevSlide();
    try {
        realtimeService.publish(roomName.value, "presentation::remote-action", { 
            action: 'select-slide',
            pageIndex: currentPageIndex.value,
            songIndex: currentSongIndex.value,
            senderId: clientId.value
        });
    } catch (e) {
        console.debug('[RealTimePresentation] Remote prev error:', e);
    }
};

const onRemoteNext = () => {
    handleNextSlide();
    try {
        realtimeService.publish(roomName.value, "presentation::remote-action", { 
            action: 'select-slide',
            pageIndex: currentPageIndex.value,
            songIndex: currentSongIndex.value,
            senderId: clientId.value
        });
    } catch (e) {
        console.debug('[RealTimePresentation] Remote next error:', e);
    }
};

const onRemoteSelectSlide = (idx, page) => {
    setCurrentPageObject(idx, page);
    try {
        realtimeService.publish(roomName.value, "presentation::remote-action", { 
            action: 'select-slide', 
            pageIndex: idx, 
            songIndex: currentSongIndex.value,
            senderId: clientId.value
        });
    } catch (e) {
        console.debug('[RealTimePresentation] Remote select slide error:', e);
    }
};

watch([currentPageIndex, currentSongIndex], async () => {
    await nextTick();
    if (activeViewMode.value === 'remote') {
        const activeEl = remoteSlideRefs.value[currentPageIndex.value];
        if (activeEl && typeof activeEl.scrollIntoView === 'function') {
            activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }
});

const selectScreen = (screen) => {
  try {
    if (!docV2.value) docV2.value = normalizeToV2(null, { playlistId: route.params.id });
    docV2.value.configuration = { ...(docV2.value.configuration || {}), screen };
    containerSize.value = calculateContainerSize();
    savePresentation();
    if (currentPageObject.value) {
      sendMessageToViewer(currentPageObject.value);
    }
  } catch (err) {
    console.error('[Presentation] selectScreen:', err);
    toastStore.dispatch('addMessage', {
      message: `Screen error: ${err?.message || err}`,
      icon: 'error',
      ttl: 4000,
    });
  }
};

const linesOptions = [
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
];

const selectLinesPerSlide = (size) => {
  try {
    const n = Number(size) || 2;
    groupSize.value = n;
    if (!docV2.value) docV2.value = normalizeToV2(null, { playlistId: route.params.id });
    docV2.value.configuration = { ...(docV2.value.configuration || {}), linesPerSlide: n };
    if (!presentationGroup.value.presentations.length) {
      toastStore.dispatch('addMessage', {
        message: t('presentation.noSongs'),
        icon: 'info',
        ttl: 3000,
      });
      return;
    }
    // Refatia todas as músicas (internamente em shape v1) e recarrega a atual
    presentationGroup.value.presentations.forEach((p) => {
      p.pages = regroupPages(p.pages, n).map(pageToV1);
      p.pages.forEach((pg) => {
        pg.effect = effectsEnabled.value;
        ensurePageLayers(pg);
      });
    });
    // Identidade fresca garante que o rail re-renderize
    loadSong(currentSongIndex.value);
    syncViewFromState();
    savePresentation();
  } catch (err) {
    console.error('[Presentation] selectLinesPerSlide:', err);
    toastStore.dispatch('addMessage', {
      message: `Lines per slide error: ${err?.message || err}`,
      icon: 'error',
      ttl: 4000,
    });
  }
};

const selectedTemplate = ref('standard');
const templateTarget = ref('all');

const templateOptions = computed(() =>
  BUILT_IN_TEMPLATES.map((tpl) => ({ value: tpl.id, label: t(tpl.labelKey) }))
);

const templateTargetOptions = computed(() =>
  TEMPLATE_TARGETS.map((tgt) => ({ value: tgt.value, label: t(tgt.labelKey) }))
);

const isStylablePage = (pg) => !!pg && pg.editable !== false;

// Aplica template no nível v2 (global/grupo/página) e espelha nas páginas
// internas v1 para renderização imediata; o save reconstrói o doc.
const applyTemplateToPages = (pages, cfg) => {
  pages.forEach((pg) => {
    if (!isStylablePage(pg)) return;
    if (cfg.background !== undefined) pg.background = cfg.background;
    if (cfg.effect !== undefined && cfg.effect !== null) pg.effect = cfg.effect;
    pg.style = { ...(pg.style || {}), ...(cfg.color ? { color: cfg.color } : {}) };
    if (cfg.color === '') {
      if (pg.style) delete pg.style.color;
      if (Object.keys(pg.style).length === 0) delete pg.style;
      // reset total: limpa cor inline colada no HTML (rail x stage divergem nela)
      if (typeof pg.content === 'string') pg.content = stripInlineColors(pg.content);
    }
  });
};

const applyTemplate = () => {
  try {
    const tpl = getTemplate(selectedTemplate.value);
    if (!tpl) return;
    const cfg = tpl.configuration || {};
    const target = templateTarget.value;
    if (!docV2.value) docV2.value = normalizeToV2(null, { playlistId: route.params.id });

  const mergeCfg = (prev) => {
    const style = { ...((prev || {}).style || {}) };
    if (cfg.color) style.color = cfg.color;
    else if (cfg.color === '') delete style.color;
    return {
      ...(prev || {}),
      ...(cfg.background !== undefined ? { background: cfg.background } : {}),
      ...(cfg.effect !== undefined && cfg.effect !== null ? { effect: cfg.effect } : {}),
      style,
    };
  };

    if (target === 'all') {
      docV2.value.configuration = mergeCfg(docV2.value.configuration);
      presentationGroup.value.presentations.forEach((p) => applyTemplateToPages(p.pages, cfg));
    } else if (target === 'group') {
      const g = docV2.value.groups?.[currentSongIndex.value];
      if (g) g.configuration = mergeCfg(g.configuration);
      const pres = presentationGroup.value.presentations[currentSongIndex.value];
      if (pres) applyTemplateToPages(pres.pages, cfg);
    } else {
      const pages = presentationGroup.value.presentations[currentSongIndex.value]?.pages;
      const pg = pages?.[currentPageIndex.value];
      if (pg && isStylablePage(pg)) applyTemplateToPages([pg], cfg);
    }
    loadSong(currentSongIndex.value);
    syncViewFromState();
    savePresentation();
  } catch (err) {
    console.error('[Presentation] applyTemplate:', err);
    toastStore.dispatch('addMessage', {
      message: `Template error: ${err?.message || err}`,
      icon: 'error',
      ttl: 4000,
    });
  }
};

const selectTemplate = (id) => {
  selectedTemplate.value = id;
  applyTemplate();
};

const selectTemplateTarget = (target) => {
  templateTarget.value = target;
  applyTemplate();
};
const allowSendSlides = ref(true);

const generateInitialRoomId = () => {
    if (route.query.room) return String(route.query.room).trim();
    return Math.floor(1000 + Math.random() * 9000).toString();
};

const roomId = ref(generateInitialRoomId());
const roomName = computed(() => "presentation::" + roomId.value);
const presentationSyncModal = ref(null);

const isWebRTCActive = ref(false);
const isWebRTCStarting = ref(false);
const webrtcPeerCount = ref(0);
let webrtcPollInterval = null;

const updateWebRTCPeerCount = () => {
    try {
        const webrtc = realtimeService.getWebRTC();
        webrtcPeerCount.value = webrtc.getConnectedCount();
        isWebRTCActive.value = webrtc.role === 'host' || webrtc.isHost || webrtcPeerCount.value > 0;
    } catch (e) {
        webrtcPeerCount.value = 0;
    }
};

const startWebRTCHost = async (customPin = null) => {
    if (isWebRTCStarting.value) return;
    isWebRTCStarting.value = true;
    try {
        const webrtc = realtimeService.getWebRTC();
        const pinToUse = customPin || (/^\d{4,6}$/.test(roomId.value) ? roomId.value : null);
        const pin = await webrtc.startHostPinSession(pinToUse);
        if (pin && pin !== roomId.value) {
            roomId.value = pin;
            subscribeToRoom();
        }
        isWebRTCActive.value = true;

        if (webrtcPollInterval) clearInterval(webrtcPollInterval);
        webrtcPollInterval = setInterval(async () => {
            try {
                await webrtc.pollHostAnswers();
                updateWebRTCPeerCount();
            } catch (err) {
                console.debug('[Presentation] pollHostAnswers error:', err);
            }
        }, 1500);
        updateWebRTCPeerCount();
    } catch (err) {
        console.error('[Presentation] Error starting WebRTC host:', err);
        isWebRTCActive.value = false;
    } finally {
        isWebRTCStarting.value = false;
    }
};

const disconnectWebRTC = () => {
    try {
        if (webrtcPollInterval) {
            clearInterval(webrtcPollInterval);
            webrtcPollInterval = null;
        }
        const webrtc = realtimeService.getWebRTC();
        if (webrtc && typeof webrtc.disconnect === 'function') {
            webrtc.disconnect();
        }
        isWebRTCActive.value = false;
        webrtcPeerCount.value = 0;
        toastStore.dispatch('addMessage', {
            message: t('stageSync.toastDisconnected') || 'Disconnected from Local Network.',
            icon: 'info',
            ttl: 2500
        });
    } catch (err) {
        console.warn('[Presentation] Error disconnecting WebRTC:', err);
    }
};

const toggleWebRTC = () => {
    if (isWebRTCActive.value || isWebRTCStarting.value) {
        disconnectWebRTC();
    } else {
        startWebRTCHost();
    }
};

const openSyncModal = () => {
    if (presentationSyncModal.value) {
        presentationSyncModal.value.open();
    }
};

const onRoomIdUpdated = (newPin) => {
    if (newPin && newPin !== roomId.value) {
        roomId.value = newPin;
        subscribeToRoom();
        if (isWebRTCActive.value) {
            startWebRTCHost(newPin);
        }
    }
};

let currentSubscribedRoomName = null;

const handleIncomingRoomMessage = (payload) => {
    if (!payload) return;
    const event = payload.event;
    const data = payload.data || payload;

    // Ignore messages published by this exact instance to avoid double advancing
    if (data.senderId && data.senderId === clientId.value) {
        return;
    }

    if (event === 'presentation::remote-action' || data.action) {
        const action = data.action || data.command;
        if (action === 'next') {
            handleNextSlide();
        } else if (action === 'prev') {
            handlePrevSlide();
        } else if (action === 'select-slide') {
            if (data.songIndex !== undefined && data.songIndex !== currentSongIndex.value && songsPresentation.value[data.songIndex]) {
                onSongChange(data.songIndex, false, false);
            }
            if (data.pageIndex !== undefined && pages.value[data.pageIndex]) {
                setCurrentPageObject(data.pageIndex, pages.value[data.pageIndex]);
            }
        } else if (action === 'set-output-state') {
            if (data.state && outputState.value !== data.state) {
                setOutputState(data.state, false);
            }
        } else if (action === 'change-song') {
            if (data.songIndex !== undefined && songsPresentation.value[data.songIndex]) {
                onSongChange(data.songIndex, true, false);
            }
        }
    } else if (event === 'presentation::sync-state') {
        if (data.outputState && outputState.value !== data.outputState) {
            outputState.value = data.outputState;
        }
        if (data.currentSongIndex !== undefined && data.currentSongIndex !== currentSongIndex.value && songsPresentation.value[data.currentSongIndex]) {
            currentSongIndex.value = data.currentSongIndex;
            pages.value = presentationGroup.value.getPresentation(currentSongIndex.value)?.pages || [];
        }
        if (data.currentPageIndex !== undefined && data.currentPageIndex !== currentPageIndex.value && pages.value[data.currentPageIndex]) {
            currentPageIndex.value = data.currentPageIndex;
            currentPageObject.value = pages.value[data.currentPageIndex];
        }
    }
};

const subscribeToRoom = () => {
    if (currentSubscribedRoomName) {
        try { 
            realtimeService.unsubscribe(currentSubscribedRoomName); 
        } catch (e) {
            console.debug('[RealTimePresentation] Unsubscribe error:', e);
        }
    }
    currentSubscribedRoomName = roomName.value;
    realtimeService.subscribe(roomName.value, '*', (payload) => {
        handleIncomingRoomMessage(payload);
    }, (status) => {
        console.log(`[Presentation] Realtime status on ${roomName.value}:`, status);
    });
};

const textClass = ref('');
const effectsEnabled = ref(false);
const saving = ref(false);
const containerRef = ref(null);
const uploading = ref(false);
const imageUploadPanel = ref(null);
const copiedPage = ref(null);
const currentSongBackground = ref(null);
const currentSongBackgroundType = ref(null);
const currentSongBackgroundTransition = ref('fade');
const songBackgroundModal = ref(null);

const currentSongVideo = computed(() => {
    const pres = presentationGroup.value?.presentations?.[currentSongIndex.value];
    if (pres?.songVideo && (pres.songVideo.videoId || pres.songVideo.url)) {
        const vid = getVideoId(pres.songVideo.videoId) || getVideoId(pres.songVideo.url) || (pres.songVideo.videoId?.length === 11 ? pres.songVideo.videoId : '');
        return {
            ...pres.songVideo,
            videoId: vid || pres.songVideo.videoId || '',
            url: vid ? getYouTubeUrl(vid) : (pres.songVideo.url || ''),
        };
    }
    if (pres?.backgroundType === 'video' && pres.background) {
        const vid = getVideoId(pres.background) || (pres.background?.length === 11 ? pres.background : '');
        return {
            videoId: vid || pres.background,
            url: vid ? getYouTubeUrl(vid) : pres.background,
            loop: true,
            start: 0,
            end: null,
        };
    }
    return null;
});

const handleUpdateSongVideo = (videoConfig) => {
    const pres = presentationGroup.value?.presentations?.[currentSongIndex.value];
    if (!pres) return;
    if (videoConfig && (videoConfig.videoId || videoConfig.url)) {
        const vid = getVideoId(videoConfig.videoId) || getVideoId(videoConfig.url) || (videoConfig.videoId?.length === 11 ? videoConfig.videoId : '');
        const cleanConfig = {
            ...videoConfig,
            videoId: vid || videoConfig.videoId || '',
            url: vid ? getYouTubeUrl(vid) : (videoConfig.url || ''),
        };
        pres.songVideo = cleanConfig;
        pres.background = vid || cleanConfig.url;
        pres.backgroundType = 'video';
        pres.backgroundTransition = cleanConfig.transition || 'fade';
        currentSongBackground.value = vid || cleanConfig.url;
        currentSongBackgroundType.value = 'video';
        currentSongBackgroundTransition.value = cleanConfig.transition || 'fade';
        if (docV2.value?.groups?.[currentSongIndex.value]) {
            docV2.value.groups[currentSongIndex.value].configuration = {
                ...(docV2.value.groups[currentSongIndex.value].configuration || {}),
                songVideo: cleanConfig,
                background: vid || cleanConfig.url,
                backgroundType: 'video',
                backgroundTransition: cleanConfig.transition || 'fade',
            };
        }
    } else {
        delete pres.songVideo;
        pres.songVideo = null;
        pres.background = null;
        pres.backgroundType = null;
        currentSongBackground.value = null;
        currentSongBackgroundType.value = null;
        currentSongBackgroundTransition.value = 'fade';
        if (docV2.value?.groups?.[currentSongIndex.value]?.configuration) {
            delete docV2.value.groups[currentSongIndex.value].configuration.songVideo;
            delete docV2.value.groups[currentSongIndex.value].configuration.background;
            delete docV2.value.groups[currentSongIndex.value].configuration.backgroundType;
        }
    }
    syncViewFromState();
    savePresentation();
    if (currentPageObject.value) {
        sendMessageToViewer(currentPageObject.value);
    }
};

// Fundo efetivo do stage (apenas vídeo se houver; imagens são layers)
const stageBgSrc = computed(() => {
    const vid = currentPageObject.value?.videoId || currentSongVideo.value?.videoId;
    if (vid) {
        try {
            return getThumbnailUrl(vid, 'maxresdefault');
        } catch {
            return `https://i.ytimg.com/vi/${vid}/hqdefault.jpg`;
        }
    }
    return '';
});

const stageBgAdjust = computed(() => {
    const pageAdjust = currentPageObject.value && currentPageObject.value.backgroundAdjust
        ? currentPageObject.value.backgroundAdjust
        : null;
    if (pageAdjust) return pageAdjust;
    const allPres = presentationGroup.value ? presentationGroup.value.presentations : [];
    const currentPres = allPres[currentSongIndex.value];
    if (currentPres && currentPres.backgroundAdjust) return currentPres.backgroundAdjust;
    const docCfg = docV2.value ? docV2.value.configuration : null;
    if (docCfg && docCfg.backgroundAdjust) return docCfg.backgroundAdjust;
    return null;
});

const onLayerFocus = (layer, editor) => {
    activeEditor.value = editor;
    if (layer) {
        selectedLayerId.value = layer.id;
        selectedLayerIds.value = [layer.id];
    }
};

// Layers da página atual para o painel (normalizadas para leitura)
const currentPageLayers = computed(() => {
    const pg = currentPageObject.value;
    if (!pg || typeof pg !== 'object') return [];
    if (Array.isArray(pg.layers) && pg.layers.length > 0) return normalizeLayers(pg.layers);
    return [];
});

// Layers da página atual em OBJETOS ORIGINAIS (sem cópia normalizada):
// mutação pinta ao vivo. Defaults aplicados inline no template.
const liveLayers = computed(() => {
    const pg = currentPageObject.value;
    if (!pg || typeof pg !== 'object' || !Array.isArray(pg.layers)) return [];
    return pg.layers.filter((l) => l && typeof l === 'object');
});

const liveLayersVisible = computed(() =>
    liveLayers.value.filter((l) => l.visible !== false)
);

const isImageLayer = (layer) => layer && layer.type === 'image';

const isLayerSelected = (id) => id === selectedLayerId.value || selectedLayerIds.value.includes(id);

// Garante box (mutando o original) para interação/cálculo ao vivo.
// Usa o helper compartilhado (mesmo do rail/output).
const ensureBox = (layer) => {
  layerBox(layer);
  return layer.box;
};

const boxOf = (layer) => layerBox(layer);

const isEmptyLayer = (layer) => {
    if (!layer || layer.type === 'image' || layer.type === 'video') return false;
    const html = layer.html ?? '';
    const text = String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return text.length === 0;
};

// Estilo do texto da box: base da página + override da layer
const boxTextStyle = (layer) => {
    const role = layer?.role;
    let defaultFontSize = '8cqi';
    let defaultColor = '#FFFFFF';
    if (role === 'header' || role === 'title') {
        defaultColor = '#60A5FA';
        defaultFontSize = '4.2cqi';
    } else if (role === 'footer' || role === 'subtitle') {
        defaultColor = '#FDE68A';
        defaultFontSize = '4.2cqi';
    }
    const base = { color: defaultColor, fontSize: defaultFontSize, textAlign: 'center' };
    const ps = currentPageObject.value?.style;
    if (ps && typeof ps === 'object') Object.assign(base, ps);
    const ls = layer && layer.style && typeof layer.style === 'object' ? layer.style : {};
    const merged = { ...base, ...ls };
    const out = {};
    if (merged.color) out.color = merged.color;
    if (merged.fontFamily) out.fontFamily = merged.fontFamily;
    if (merged.fontSize) {
        const px = parseFloat(merged.fontSize);
        if (Number.isFinite(px) && px > 0) {
            out.fontSize = `${(px / 800) * 100}cqi`;
        } else {
            out.fontSize = merged.fontSize;
        }
    } else {
        out.fontSize = defaultFontSize;
    }
    if (merged.textAlign) out.textAlign = merged.textAlign;
    Object.assign(out, layerStyle(layer));
    return out;
};

const imagePointerDown = (e, layer) => {
    if (!layer || layer.locked) return;
    toggleLayerSelect(layer.id, e);
    ensureBox(layer);
    boxMoveStart(e, layer);
};

const handleReplicateLayers = () => {
    const ids = selectedLayerIds.value.length > 0
        ? [...selectedLayerIds.value]
        : (selectedLayerId.value ? [selectedLayerId.value] : []);
    if (ids.length === 0) {
        toastStore.dispatch('addMessage', {
            message: t('presentation.selectLayerFirst'),
            icon: 'info',
            ttl: 2500,
        });
        return;
    }
    const source = [];
    const pg = currentPageObject.value;
    (pg?.layers || []).forEach((l) => {
        if (ids.includes(l.id)) source.push(JSON.parse(JSON.stringify(l)));
    });
    if (source.length === 0) return;
    // Escopo: só a música atual (todas as páginas exceto a de origem)
    const pres = presentationGroup.value.presentations[currentSongIndex.value];
    if (!pres) return;
    let count = 0;
    (pres.pages || []).forEach((p) => {
        if (!p || p.editable === false || p === pg) return;
        if (!Array.isArray(p.layers)) p.layers = [];
        source.forEach((s) => {
            const copy = JSON.parse(JSON.stringify(s));
            copy.id = `l_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
            p.layers.push(copy);
            count += 1;
        });
    });
    toastStore.dispatch('addMessage', {
        message: t('presentation.replicatedCount', { count }),
        icon: 'success',
        ttl: 2500,
    });
    persistLayers();
};

const resetting = ref(false);

const resetPresentation = async () => {
    const confirmed = await dialogStore.dispatch('confirm', {
        title: t('presentation.resetTitle'),
        message: t('presentation.resetConfirm'),
    });
    if (!confirmed) return;
    resetting.value = true;
    try {
        const response = await presentationService.recreatePresentation(route.params.id);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        clearLayerSelection();
        await loadData();
        effectsEnabled.value = false;
        toastStore.dispatch('addMessage', {
            message: t('presentation.resetDone'),
            icon: 'success',
            ttl: 3000,
        });
    } catch (err) {
        console.error('[Presentation] reset:', err);
        toastStore.dispatch('addMessage', {
            message: t('presentation.resetError'),
            icon: 'error',
            ttl: 4000,
        });
    } finally {
        resetting.value = false;
    }
};

// Seleção múltipla (ctrl/cmd+click soma; clique simples isola)
const selectedLayerIds = ref([]);

const toggleLayerSelect = (id, evt) => {
    if (evt && (evt.ctrlKey || evt.metaKey)) {
        const i = selectedLayerIds.value.indexOf(id);
        if (i === -1) selectedLayerIds.value.push(id);
        else selectedLayerIds.value.splice(i, 1);
        selectedLayerId.value = selectedLayerIds.value[selectedLayerIds.value.length - 1] || null;
    } else {
        selectedLayerId.value = id;
        selectedLayerIds.value = id ? [id] : [];
    }
};

const clearLayerSelection = () => {
    selectedLayerId.value = null;
    selectedLayerIds.value = [];
};

const currentPageArray = () => presentationGroup.value.presentations[currentSongIndex.value]?.pages || [];
const currentInternalPage = () => currentPageArray()[currentPageIndex.value] || null;

const persistLayers = () => {
    syncViewFromState();
    savePresentation();
};

const handleToggleLayerVisibility = (id) => {
    const pg = currentInternalPage();
    const layer = pg?.layers?.find((l) => l.id === id);
    if (!layer) return;
    layer.visible = layer.visible === false;
    persistLayers();
};

const handleToggleLayerLock = (id) => {
    const pg = currentInternalPage();
    const layer = pg?.layers?.find((l) => l.id === id);
    if (!layer) return;
    layer.locked = !layer.locked;
    persistLayers();
};

const handleDeleteLayer = (id) => {
    const pg = currentInternalPage();
    if (!pg || !Array.isArray(pg.layers)) return;
    pg.layers = pg.layers.filter((l) => l.id !== id);
    if (selectedLayerId.value === id) selectedLayerId.value = null;
    persistLayers();
};

const handleRenameLayer = ({ id, name }) => {
    const pg = currentInternalPage();
    const layer = pg?.layers?.find((l) => l.id === id);
    if (!layer) return;
    layer.name = name;
    persistLayers();
};

const handleUpdateLayerStyle = ({ id, patch }) => {
    const pg = currentInternalPage();
    const layer = pg?.layers?.find((l) => l.id === id);
    if (!layer) return;
    Object.assign(layer, patch);
    persistLayers();
};

const handleReorderLayers = ({ oldIndex, newIndex }) => {
    const pg = currentInternalPage();
    if (!pg || !Array.isArray(pg.layers)) return;
    const [moved] = pg.layers.splice(oldIndex, 1);
    pg.layers.splice(newIndex, 0, moved);
    persistLayers();
};

const pendingLayerGroup = ref('primary');

const handleAddTextBox = (group = 'primary') => {
    const pg = currentInternalPage();
    if (!pg) return;
    if (!Array.isArray(pg.layers)) pg.layers = [];
    const n = pg.layers.filter((l) => l.type === 'text').length;
    const box = createTextBox({
        name: `Texto ${n + 1}`,
        group: typeof group === 'string' && group === 'secondary' ? 'secondary' : 'primary',
        box: { x: 10 + (n % 4) * 4, y: 24 + (n % 4) * 5, w: 80 },
    });
    pg.layers.push(box);
    selectedLayerId.value = box.id;
    persistLayers();
};

const requestImageLayer = (group = 'primary') => {
    pendingLayerGroup.value = typeof group === 'string' && group === 'secondary' ? 'secondary' : 'primary';
    if (songBackgroundModal.value) {
        songBackgroundModal.value.open({});
    }
};

const handleAddVideoLayer = (value = '', group = null) => {
    const pg = currentInternalPage();
    if (!pg) return;
    if (!Array.isArray(pg.layers)) pg.layers = [];
    const val = typeof value === 'string' ? value : '';
    const videoId = val ? (getVideoId(val) || (val.length === 11 ? val : '')) : '';
    const cleanUrl = videoId ? getYouTubeUrl(videoId) : val;
    const n = pg.layers.filter((l) => l.type === 'video').length;
    const targetGroup = group !== null && group !== undefined
        ? (group === 'secondary' ? 'secondary' : 'primary')
        : (pendingLayerGroup.value || 'primary');
    const layer = createVideoLayer({
        name: `Vídeo ${n + 1}`,
        url: cleanUrl,
        videoId,
        loop: false,
        start: 0,
        end: null,
        group: targetGroup,
        box: { x: 10, y: 15, w: 80 },
    });
    pg.layers.push(layer);
    selectedLayerId.value = layer.id;
    selectedLayerIds.value = [layer.id];
    persistLayers();
};

const handleAddImageLayer = (src, group = null) => {
    const pg = currentInternalPage();
    if (!pg || !src) return;
    if (!Array.isArray(pg.layers)) pg.layers = [];
    const n = pg.layers.filter((l) => l.type === 'image').length;
    const targetGroup = group !== null && group !== undefined
        ? (group === 'secondary' ? 'secondary' : 'primary')
        : (pendingLayerGroup.value || 'primary');
    const layer = createImageLayer({
        name: `Imagem ${n + 1}`,
        src,
        group: targetGroup,
    });
    pg.layers.push(layer);
    selectedLayerId.value = layer.id;
    selectedLayerIds.value = [layer.id];
    persistLayers();
};

const onLayerInput = (layer, html) => {
    if (!layer || layer.html === html) return;
    layer.html = html;
    // sincroniza o objeto interno (mesma referência do array em geral)
    const pg = currentPageObject.value;
    if (pg && Array.isArray(pg.layers)) {
        const mine = pg.layers.find((l) => l.id === layer.id);
        if (mine) mine.html = html;
    }
    updateSongPresentation();
};

// Seleção e drag das caixas de texto no canvas
const selectedLayerId = ref(null);

let boxDrag = null;

const boxMoveStart = (e, layer) => {
    if (!layer || layer.locked) return;
    e.preventDefault();
    boxDrag = {
        kind: 'move',
        startX: e.clientX,
        startY: e.clientY,
        ox: layer.box.x,
        oy: layer.box.y,
        layer,
    };
    window.addEventListener('pointermove', boxDragMove);
    window.addEventListener('pointerup', boxDragEnd, { once: true });
};

const boxResizeStart = (e, layer) => {
    if (!layer || layer.locked) return;
    e.preventDefault();
    e.stopPropagation();
    boxDrag = {
        kind: 'resize',
        startX: e.clientX,
        startW: layer.box.w,
        layer,
    };
    window.addEventListener('pointermove', boxDragMove);
    window.addEventListener('pointerup', boxDragEnd, { once: true });
};

const boxDragMove = (e) => {
    if (!boxDrag || !containerSize.value.width) return;
    const { layer } = boxDrag;
    if (boxDrag.kind === 'move') {
        layer.box.x = Math.round(((boxDrag.ox * containerSize.value.width / 100) + (e.clientX - boxDrag.startX)) / containerSize.value.width * 1000) / 10;
        layer.box.y = Math.round(((boxDrag.oy * containerSize.value.height / 100) + (e.clientY - boxDrag.startY)) / containerSize.value.height * 1000) / 10;
    } else {
        const dw = ((e.clientX - boxDrag.startX) / containerSize.value.width) * 100;
        layer.box.w = Math.max(10, Math.round((boxDrag.startW + dw) * 10) / 10);
    }
};

const boxDragEnd = () => {
    boxDrag = null;
    window.removeEventListener('pointermove', boxDragMove);
    syncViewFromState();
    savePresentation();
};

const handleAlignLayer = (type) => {
    const pg = currentInternalPage();
    if (!pg || !Array.isArray(pg.layers) || pg.layers.length === 0) return;
    
    const targetIds = selectedLayerIds.value.length > 0
        ? selectedLayerIds.value
        : (selectedLayerId.value ? [selectedLayerId.value] : []);
    
    let targets = pg.layers.filter((l) => targetIds.includes(l.id));
    if (targets.length === 0) {
        const contentLayer = pg.layers.find((l) => l.type === 'text' && l.role === 'content' && l.visible !== false)
            || pg.layers.find((l) => l.type === 'text' && l.visible !== false);
        if (contentLayer) targets = [contentLayer];
    }
    if (targets.length === 0) return;

    // Collect bounds for all target layers
    const items = targets.map((layer) => {
        ensureBox(layer);
        let w = Number(layer.box.w) || 90;
        let h = 35;
        if (containerRef.value) {
            const el = containerRef.value.querySelector(`[data-layer-id="${layer.id}"]`);
            if (el) {
                const cRect = containerRef.value.getBoundingClientRect();
                const lRect = el.getBoundingClientRect();
                if (cRect.height > 0 && lRect.height > 0) {
                    h = (lRect.height / cRect.height) * 100;
                }
            }
        }
        const x = Number(layer.box.x) || 0;
        const y = Number(layer.box.y) || 0;
        return {
            layer,
            x,
            y,
            w,
            h,
            right: x + w,
            bottom: y + h,
        };
    });

    if (items.length === 1) {
        // Single layer: align relative to slide / container
        const item = items[0];
        const { layer, w, h } = item;
        switch (type) {
            case 'top':
                layer.box.y = 4;
                break;
            case 'middle':
                layer.box.y = Math.max(0, Math.round(((100 - h) / 2) * 10) / 10);
                break;
            case 'bottom':
                layer.box.y = Math.max(0, Math.round((96 - h) * 10) / 10);
                break;
            case 'left':
                layer.box.x = 4;
                break;
            case 'center':
                layer.box.x = Math.max(0, Math.round(((100 - w) / 2) * 10) / 10);
                break;
            case 'right':
                layer.box.x = Math.max(0, Math.round((96 - w) * 10) / 10);
                break;
        }
    } else {
        // Multiple layers: align relative to selection bounding box
        const minX = Math.min(...items.map((i) => i.x));
        const maxX = Math.max(...items.map((i) => i.right));
        const minY = Math.min(...items.map((i) => i.y));
        const maxY = Math.max(...items.map((i) => i.bottom));
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        items.forEach((item) => {
            const { layer, w, h } = item;
            switch (type) {
                case 'top':
                    layer.box.y = Math.round(minY * 10) / 10;
                    break;
                case 'middle':
                    layer.box.y = Math.max(0, Math.round((centerY - h / 2) * 10) / 10);
                    break;
                case 'bottom':
                    layer.box.y = Math.max(0, Math.round((maxY - h) * 10) / 10);
                    break;
                case 'left':
                    layer.box.x = Math.round(minX * 10) / 10;
                    break;
                case 'center':
                    layer.box.x = Math.max(0, Math.round((centerX - w / 2) * 10) / 10);
                    break;
                case 'right':
                    layer.box.x = Math.max(0, Math.round((maxX - w) * 10) / 10);
                    break;
            }
        });
    }

    persistLayers();
};

const handleTextAlign = (align) => {
    const pg = currentInternalPage();
    if (!pg || !Array.isArray(pg.layers)) return;
    const targetIds = selectedLayerIds.value.length > 0
        ? selectedLayerIds.value
        : (selectedLayerId.value ? [selectedLayerId.value] : []);
    
    let targets = pg.layers.filter((l) => targetIds.includes(l.id));
    if (targets.length === 0) {
        const contentLayer = pg.layers.find((l) => l.type === 'text' && l.role === 'content' && l.visible !== false)
            || pg.layers.find((l) => l.type === 'text' && l.visible !== false);
        if (contentLayer) targets = [contentLayer];
    }

    targets.forEach((l) => {
        if (!l.style) l.style = {};
        l.style.textAlign = align;
    });
    persistLayers();
};

const containerSize = ref({ width: 0, height: 0 });

const containerStyle = computed(() => {
    const rawScreen = String(docV2.value?.configuration?.screen || '4:3');
    const [w, h] = rawScreen.includes('/') ? rawScreen.split('/').map(Number) : rawScreen.split(':').map(Number);
    const aspect = (w > 0 && h > 0) ? `${w} / ${h}` : '4 / 3';
    
    if (containerSize.value.width > 50 && containerSize.value.height > 50) {
        return {
            width: `${containerSize.value.width}px`,
            height: `${containerSize.value.height}px`,
            aspectRatio: aspect,
            containerType: 'size',
        };
    }
    return {
        width: '100%',
        maxWidth: '720px',
        maxHeight: 'calc(100% - 130px)',
        aspectRatio: aspect,
        containerType: 'size',
    };
});

let parentResizeObserver = null;

const setupResizeObserver = () => {
    if (typeof ResizeObserver === 'undefined') return;
    const parent = containerRef.value?.parentElement;
    if (!parent) return;
    if (parentResizeObserver) {
        parentResizeObserver.disconnect();
    }
    parentResizeObserver = new ResizeObserver(() => {
        handleResize();
    });
    parentResizeObserver.observe(parent);
};

const handleResize = () => {
    checkDevice();
    containerSize.value = calculateContainerSize();
};

onMounted(async () => {
    appStore.state.currentView = "Real Time Presentation";
    checkDevice();
    initScreenDetails();
    subscribeToRoom();
    await loadData();
    window.addEventListener('keydown', handleKeyDown);
    
    // Auto start WebRTC host when entering desktop mode
    if (activeViewMode.value === 'desktop') {
        startWebRTCHost();
    }

    // Calculate initial container size after DOM is ready
    await nextTick();
    handleResize();
    setupResizeObserver();
    
    window.addEventListener('resize', handleResize);
    if (window.screen && window.screen.orientation) {
        window.screen.orientation.addEventListener("change", handleResize);
    }
});
onUnmounted(() => {
    if (webrtcPollInterval) {
        clearInterval(webrtcPollInterval);
        webrtcPollInterval = null;
    }
    if (parentResizeObserver) {
        parentResizeObserver.disconnect();
        parentResizeObserver = null;
    }
    if (currentSubscribedRoomName) {
        try { 
            realtimeService.unsubscribe(currentSubscribedRoomName); 
        } catch (e) {
            console.debug('[RealTimePresentation] Unsubscribe on unmount error:', e);
        }
    }
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('resize', handleResize);
    if (window.screen && window.screen.orientation) {
        window.screen.orientation.removeEventListener("change", handleResize);
    }
});

const calculateContainerSize = () => {
    if (!containerRef.value) {
        return {
            width: 0,
            height: 0
        };
    }
    
    const aspectRatio = (() => {
        const raw = String(docV2.value?.configuration?.screen || '4:3');
        const [w, h] = raw.includes('/') ? raw.split('/').map(Number) : raw.split(':').map(Number);
        return (w > 0 && h > 0) ? (w / h) : (4 / 3);
    })();
    const parent = containerRef.value.parentElement;
    
    if (!parent) {
        return {
            width: 0,
            height: 0
        };
    }
    
    // Get parent dimensions with fallback
    const rect = parent.getBoundingClientRect();
    const parentWidth = parent.clientWidth || rect.width || 800;
    const parentHeight = parent.clientHeight || rect.height || 600;
    
    // Calculate reserved space
    const topReserved = 16 + 40; // 56px (padding + info space)
    const bottomReserved = 16 + 56; // 72px (padding + arrows/toolbar space)
    const horizontalPadding = 32 * 2; // 64px total
    
    // Calculate available space
    const availableWidth = Math.max(parentWidth - horizontalPadding, 200);
    const availableHeight = Math.max(parentHeight - topReserved - bottomReserved, 150);
    
    // Calculate maximum dimensions that fit while maintaining aspect ratio
    let newWidth = availableWidth;
    let newHeight = newWidth / aspectRatio;
    
    if (newHeight > availableHeight) {
        newHeight = availableHeight;
        newWidth = newHeight * aspectRatio;
    }
    
    if (newWidth > availableWidth) {
        newWidth = availableWidth;
        newHeight = newWidth / aspectRatio;
    }
    
    return {
        width: Math.round(newWidth),
        height: Math.round(newHeight)
    };
};

const loadData = async () => {
    // Reseta o estado (recarregamentos como o reset recriariam duplicado)
    presentationGroup.value = new PresentationGroup();
    songsPresentation.value = [];
    clearLayerSelection();
    presentationGroup.value.playlistId = route.params.id;

    let playlistSongs = [];
    try {
        const plRes = await playlistService.fetchPlaylist({ id: route.params.id });
        if (plRes && (plRes.songs || plRes.data?.songs)) {
            playlistSongs = plRes.songs || plRes.data?.songs || [];
        }
    } catch (e) {
        console.debug('[RealTimePresentation] Could not fetch playlist songs for section category detection:', e);
    }

    const response = await presentationService.loadPresentation(route.params.id);

    const data = await response.json();
    // Boundary v2: aceita doc v1 legado ou v2; edição interna segue em v1,
    // e o save persiste sempre em v2.
    docV2.value = normalizeToV2(data, { playlistId: route.params.id });
    groupSize.value = docV2.value.configuration?.linesPerSlide || 2;
    const v1 = denormalizeToV1(docV2.value);

    let categoriesInferred = false;

    v1.presentations.forEach((p, pIdx) => {
        const matchedSong = playlistSongs.find(s =>
            (s._id && (s._id === p.songId || s._id === p.id)) ||
            (s.id && (s.id === p.songId || s.id === p.id)) ||
            (s.songId && (s.songId === p.songId || s.songId === p.id))
        ) || playlistSongs[pIdx];

        let songSections = [];
        if (matchedSong) {
            const rawLyric = matchedSong.modifiedLyric || matchedSong.data?.content || matchedSong.data?.lyric || matchedSong.lyric;
            if (rawLyric) {
                songSections = extractSongSectionsFromLyric(rawLyric);
            }
        }

        const presentation = new Presentation(p);
        // ajuste de fundo da música (campo fora do construtor legado)
        presentation.backgroundAdjust = p.backgroundAdjust ?? null;
        presentation.songVideo = p.songVideo ?? null;
        presentation.groupPages(groupSize.value);
        presentation.pages.forEach(ensurePageLayers);

        // Auto-detect and populate missing categories for pages
        presentation.pages.forEach((page) => {
            if (!page.category && page.editable !== false && page.id !== '_blank') {
                const detected = detectSlideCategory(page) ||
                                 detectSlideCategory(page.content) ||
                                 (songSections.length > 0 ? matchSlideCategoryFromSongSections(page.content, songSections) : null);
                if (detected) {
                    page.category = detected;
                    categoriesInferred = true;
                }
            }
        });

        presentationGroup.value.addPresentation(presentation);
    });

    songsPresentation.value = v1.presentations;
    
    currentSongIndex.value = 0;
    if (presentationGroup.value.presentations.length > 0) {
        effectsEnabled.value = presentationGroup.value.getPresentation(currentSongIndex.value).pages.some(page => page.effect);
    } 
    
    loadSong(0);
    loading.value = false;
    presentationLoaded.value = true;
    await nextTick();
    handleResize();
    setupResizeObserver();

    if (categoriesInferred) {
        savePresentation();
    }
};

const loadSong = (songIndex, goToFirstPage = true) => {
    currentSongIndex.value = songIndex;
    pages.value = presentationGroup.value.getPresentation(songIndex).pages;
    const song = presentationGroup.value.presentations[songIndex];
    if (song?.songVideo && (song.songVideo.videoId || song.songVideo.url)) {
        const vid = getVideoId(song.songVideo.videoId) || getVideoId(song.songVideo.url) || (song.songVideo.videoId?.length === 11 ? song.songVideo.videoId : '');
        currentSongBackground.value = vid || song.songVideo.videoId || song.songVideo.url;
        currentSongBackgroundType.value = 'video';
        currentSongBackgroundTransition.value = song.songVideo.transition || song.backgroundTransition || 'fade';
    } else if (song?.backgroundType === 'image') {
        currentSongBackground.value = song.background;
        currentSongBackgroundType.value = 'image';
        currentSongBackgroundTransition.value = song.backgroundTransition || 'fade';
    } else if (song?.backgroundType === 'video') {
        const vid = getVideoId(song.background) || (song.background?.length === 11 ? song.background : '');
        currentSongBackground.value = vid || song.background;
        currentSongBackgroundType.value = 'video';
        currentSongBackgroundTransition.value = song.backgroundTransition || 'fade';
    } else {
        currentSongBackground.value = null;
        currentSongBackgroundType.value = null;
        currentSongBackgroundTransition.value = 'fade';
    }
    if (goToFirstPage) {
        currentPageIndex.value = 0;
    } else {
        currentPageIndex.value = pages.value.length - 1;
    }
    // Re-aponta o objeto atual (inputs do editor clonam via createPageObject;
    // sem isso rail e stage podem segurar objetos diferentes da mesma página)
    currentPageObject.value = pages.value[currentPageIndex.value] || null;

    return true;
}

// Fonte única rail/stage: nova identidade (dispara o watcher do rail) +
// re-aponta o objeto atual. Chamar após toda mutação de páginas.
const syncViewFromState = () => {
    const pres = presentationGroup.value.presentations[currentSongIndex.value];
    if (!pres) return;
    pages.value = [...pres.pages];
    const idx = Math.min(Math.max(0, currentPageIndex.value), pages.value.length - 1);
    currentPageIndex.value = idx;
    currentPageObject.value = pages.value[idx] || null;
};

const onSongChange = (songIndex = null, goToFirstPage = true, broadcast = true) => {
    const targetIndex = (songIndex !== null && songIndex !== undefined) ? Number(songIndex) : currentSongIndex.value;
    if (isNaN(targetIndex) || targetIndex < 0 || targetIndex >= songsPresentation.value.length) return;

    loadSong(targetIndex, goToFirstPage);

    if (broadcast) {
        try {
            realtimeService.publish(roomName.value, "presentation::remote-action", {
                action: 'change-song',
                songIndex: targetIndex
            });
        } catch (e) {
            console.debug('[RealTimePresentation] change-song error:', e);
        }
    }

    if (currentPageObject.value) {
        sendMessageToViewer(currentPageObject.value);
    }
};

const handleSongsReorder = ({ oldIndex, newIndex, options }) => {
    // Update songsPresentation array with reordered items
    songsPresentation.value = options;
    
    // Reorder presentationGroup to match the new order
    const presentations = [...presentationGroup.value.presentations];
    const [movedPresentation] = presentations.splice(oldIndex, 1);
    presentations.splice(newIndex, 0, movedPresentation);
    presentationGroup.value.presentations = presentations;
    
    // Update current song index if it was affected by the reorder
    if (oldIndex === currentSongIndex.value) {
        currentSongIndex.value = newIndex;
    } else if (oldIndex < currentSongIndex.value && newIndex >= currentSongIndex.value) {
        currentSongIndex.value--;
    } else if (oldIndex > currentSongIndex.value && newIndex <= currentSongIndex.value) {
        currentSongIndex.value++;
    }
    
    // Reload the current song to ensure everything is in sync
    loadSong(currentSongIndex.value);
    syncViewFromState();

    // Save the reordered presentation
    savePresentation();
}

const setCurrentPageObject = (idx, page) => {

    currentPageObject.value = page;
    currentPageIndex.value = idx;

    sendMessageToViewer(page);
}

const goToNext = () => {
    const nextSongIndex = currentSongIndex.value + 1;
    if (nextSongIndex < songsPresentation.value.length) {
        loadSong(nextSongIndex, true);
        if (currentPageObject.value) {
            sendMessageToViewer(currentPageObject.value);
        }
    }
    // If at the end, do nothing (just return)
}

const backToPrevious = () => {
    const prevSongIndex = currentSongIndex.value - 1;
    if (prevSongIndex >= 0) {
        loadSong(prevSongIndex, false);
        if (currentPageObject.value) {
            sendMessageToViewer(currentPageObject.value);
        }
    }
    // If at the beginning, do nothing (just return)
}

const handlePageReorder = async ({ oldIndex, newIndex}) => {
    presentationGroup.value.getPresentation(currentSongIndex.value).movePageAtIndex(oldIndex, newIndex);
    pages.value = presentationGroup.value.getPresentation(currentSongIndex.value).pages;
}
const updateSongPresentation = () => {
    presentationGroup.value.getPresentation(currentSongIndex.value).pages[currentPageIndex.value] = currentPageObject.value;
    pages.value = presentationGroup.value.getPresentation(currentSongIndex.value).pages;
}
watch(() => currentPageIndex.value, (newVal) => {
    currentPageObject.value = presentationGroup.value.getPresentation(currentSongIndex.value).pages[newVal];
});
const handlePageCategoryUpdate = ({ index, category }) => {
    if (presentationGroup.value.getPresentation(currentSongIndex.value)?.pages[index]) {
        presentationGroup.value.getPresentation(currentSongIndex.value).pages[index].category = category;
        if (currentPageIndex.value === index && currentPageObject.value) {
            currentPageObject.value.category = category;
        }
        savePresentation();
    }
};

const handleKeyDown = (event) => {
    if (!pages.value || pages.value.length === 0) {
        return
    }
    const activeEl = document.activeElement;
    const isTyping = activeEl && (
        activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        activeEl.isContentEditable ||
        activeEl.closest('.presentation-editor') ||
        activeEl.closest('.ProseMirror') ||
        document.querySelectorAll('.presentation-editor .ProseMirror-focused').length > 0
    );
    if (isTyping) {
        return;
    }

    if ((event.metaKey || event.ctrlKey) && event.key === 'c') {
        const page = presentationGroup.value.getPresentation(currentSongIndex.value).pages[currentPageIndex.value];
        if (!page.editable) return;
        
        event.preventDefault();
        copiedPage.value = { ...page };
        toastStore.dispatch('addMessage', {
            message: 'Page Copied',
            icon: 'info',
            ttl: 2000
        });
        return;
    }

    // Paste Page
    if ((event.metaKey || event.ctrlKey) && event.key === 'v') {
        if (!copiedPage.value) return;
        event.preventDefault();
        
        const newPage = { ...copiedPage.value };
        const insertIndex = currentPageIndex.value + 1;
        
        presentationGroup.value.getPresentation(currentSongIndex.value).addPageAtIndex(insertIndex, newPage);
        pages.value = presentationGroup.value.getPresentation(currentSongIndex.value).pages;
        
        currentPageIndex.value = insertIndex;
        currentPageObject.value = presentationGroup.value.getPresentation(currentSongIndex.value).pages[insertIndex];
        
        savePresentation();
        
        toastStore.dispatch('addMessage', {
            message: 'Page Pasted',
            icon: 'success',
            ttl: 2000
        });
        return;
    }

    // Category Jump Shortcuts without modifier keys (c: Chorus, v: Verse, b: Bridge, i: Intro, p: Pre-Chorus, o: Outro, t: Tag)
    if (!event.metaKey && !event.ctrlKey && !event.altKey) {
        const cat = getCategoryByShortcut(event.key);
        if (cat) {
            const nextIdx = findNextSlideByCategory(pages.value, currentPageIndex.value, cat.id);
            if (nextIdx !== null) {
                event.preventDefault();
                const page = presentationGroup.value.getPresentation(currentSongIndex.value).pages[nextIdx];
                setCurrentPageObject(nextIdx, page);
                toastStore.dispatch('addMessage', {
                    message: `${t(cat.labelKey) || cat.defaultLabel} (${nextIdx + 1})`,
                    icon: 'info',
                    ttl: 1200
                });
                return;
            }
        }
    }

    if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault();
        handleNextSlide();
    } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        handlePrevSlide();
    }
}

// Próxima página visível na direção (pula `skipped`); null se não houver.
// Blank nunca é skipped, então sempre há ao menos um destino válido por música.
const nextVisiblePage = (dir) => {
    let idx = currentPageIndex.value + dir;
    while (idx >= 0 && idx < pages.value.length) {
        if (!pages.value[idx]?.skipped) return idx;
        idx += dir;
    }
    return null;
};

const handleNextSlide = () => {
    const next = nextVisiblePage(1);
    if (next === null) {
        goToNext();
        return;
    }
    const page = presentationGroup.value.getPresentation(currentSongIndex.value).pages[next];
    setCurrentPageObject(next, page);
};

const handlePrevSlide = () => {
    const prev = nextVisiblePage(-1);
    if (prev === null) {
        backToPrevious();
        return;
    }
    const page = presentationGroup.value.getPresentation(currentSongIndex.value).pages[prev];
    setCurrentPageObject(prev, page);
};

const handleToggleSkipPage = (idx) => {
    const pg = presentationGroup.value.presentations[currentSongIndex.value]?.pages?.[idx];
    if (!pg || pg.editable === false) return;
    pg.skipped = !pg.skipped;
    syncViewFromState();
    savePresentation();
};
const handleNewPage = (idx) => {
    // Slide novo: 3 caixas vazias (placeholders, não projetam até ter texto)
    const newPage = {
        title: "", content: "", subtitle: "", editable: true,
        style: { fontSize: '64px', color: '#FFFFFF', textAlign: 'center' },
        layers: newSlideLayers({
            header: t('presentation.phHeader'),
            content: t('presentation.phContent'),
            footer: t('presentation.phFooter'),
        }),
    };
    presentationGroup.value.getPresentation(currentSongIndex.value).addPageAtIndex(idx + 1, newPage);
    currentPageIndex.value = idx + 1;
    syncViewFromState();
    savePresentation();
}
const handleRemovePage = (idx) => {
    if (idx === 0) return;
    presentationGroup.value.getPresentation(currentSongIndex.value).removePageAtIndex(idx);
    currentPageIndex.value = Math.max(0, idx - 1);
    syncViewFromState();
    savePresentation();
}

const handleDuplicatePage = (idx) => {
    const pageToDuplicate = presentationGroup.value.getPresentation(currentSongIndex.value).pages[idx];
    const newPage = JSON.parse(JSON.stringify({ ...pageToDuplicate })); // deep copy (layers inclusas)
    // novos ids para não colidir no save/ordem
    if (Array.isArray(newPage.layers)) {
        newPage.layers.forEach((l) => { l.id = `${l.id || 'l'}_copy_${Date.now().toString(36)}`; });
    }
    
    presentationGroup.value.getPresentation(currentSongIndex.value).addPageAtIndex(idx + 1, newPage);
    pages.value = presentationGroup.value.getPresentation(currentSongIndex.value).pages;
    
    currentPageIndex.value = idx + 1;
    currentPageObject.value = presentationGroup.value.getPresentation(currentSongIndex.value).pages[currentPageIndex.value];
    savePresentation();
}

const convertPageToMessage = (page) => {
    let message = new Message();
    message.content = page.content;
    message.subtitle = page.subtitle;
    message.header = page.title;
    message.artist = page.artist;

    const effectsOn = docV2.value?.configuration?.effects !== false;
    message.effect = effectsOn && (page.effect !== false);
    message.effectType = docV2.value?.configuration?.effectType || docV2.value?.configuration?.transition || 'fade';
    message.effectDuration = docV2.value?.configuration?.effectDuration ?? 500;
    message.transition = message.effectType;
    message.transitionDuration = message.effectDuration;
    message.screen = docV2.value?.configuration?.screen || '16:9';
    
    // Song video (continuous) vs page video
    const sv = currentSongVideo.value;
    if (sv && (sv.videoId || sv.url)) {
        const vid = getVideoId(sv.videoId) || getVideoId(sv.url) || (sv.videoId?.length === 11 ? sv.videoId : '');
        message.songVideo = { ...sv, videoId: vid || sv.videoId || '' };
        message.videoId = vid || sv.videoId;
        message.videoConfiguration = { loop: sv.loop !== false, start: sv.start ?? 0, end: sv.end ?? null };
        message.background = vid || sv.background || sv.url;
        message.backgroundType = 'video';
        message.backgroundTransition = sv.transition || currentSongBackgroundTransition.value || 'fade';
    } else if (page.videoId) {
        const vid = getVideoId(page.videoId) || (page.videoId.length === 11 ? page.videoId : '');
        message.videoId = vid || page.videoId;
        message.videoConfiguration = page.videoConfiguration || null;
        message.background = vid || page.videoId;
        message.backgroundType = 'video';
        message.backgroundTransition = 'fade';
    } else if (page.background && isYoutubeUrl(page.background)) {
        const vid = getVideoId(page.background);
        message.videoId = vid;
        message.background = vid ? getYouTubeUrl(vid) : page.background;
        message.backgroundType = 'video';
        message.backgroundTransition = 'fade';
    } else {
        message.background = null;
        message.backgroundType = null;
    }
    message.backgroundAdjust = page.backgroundAdjust
        ?? presentationGroup.value.presentations[currentSongIndex.value]?.backgroundAdjust
        ?? null;
    // layers visíveis para o output projetar empilhado (fallback: sintetiza)
    const srcLayers = Array.isArray(page.layers) && page.layers.length > 0
        ? page.layers
        : synthesizeLayersFromLegacy(page);
    message.layers = normalizeLayers(srcLayers).filter((l) => l.visible !== false);
    return message;
}

const applyEffect = (message) => {
    if (message.effect && message.effectType !== 'none'){
        const dur = message.effectDuration || 500;
        nextTick( () => {
            textClass.value="opacity-0"
            setTimeout( () => {
                textClass.value='animated-text fade-in';
            }, dur)
        });
    } else {
        textClass.value='';
    }
}
const broadcastSyncState = () => {
    try {
        realtimeService.publish(roomName.value, "presentation::sync-state", {
            currentSongIndex: currentSongIndex.value,
            currentPageIndex: currentPageIndex.value,
            outputState: outputState.value,
            songTitle: songsPresentation.value[currentSongIndex.value]?.title || '',
            senderId: clientId.value,
            timestamp: Date.now()
        });
    } catch (e) {
        console.debug('[RealTimePresentation] broadcastSyncState error:', e);
    }
};

const sendMessageToViewer = async(page) => {
    let message = convertPageToMessage(page);
    applyEffect(message);
    broadcastSyncState();
    if (!allowSendSlides.value){
        return;
    }
    // blank/frozen/logo seguram a saída: navegação não publica por cima
    if (outputState.value !== 'live') {
        return;
    }
    try {
        realtimeService.publish(roomName.value, "presentation::next", { message: message });
    } catch(e) {
        console.log(e);
    }
}

const publishOutputState = () => {
    try {
        if (outputState.value === 'blank') {
            const message = new Message();
            message.content = '';
            message.subtitle = '';
            message.header = '';
            message.layers = [];
            message.effect = false;
            message.clean = true;
            realtimeService.publish(roomName.value, "presentation::next", { message });
        } else if (outputState.value === 'logo') {
            const message = new Message();
            message.content = '';
            message.subtitle = '';
            message.header = '';
            message.layers = [];
            message.effect = false;
            message.clean = true;
            message.logo = true;
            message.logoUrl = docV2.value?.configuration?.logoUrl || null;
            realtimeService.publish(roomName.value, "presentation::next", { message });
        }
    } catch(e) {
        console.log(e);
    }
};

const setOutputState = (state, broadcast = true) => {
    outputState.value = state;
    if (broadcast) {
        try {
            realtimeService.publish(roomName.value, "presentation::remote-action", { 
                action: 'set-output-state', 
                state,
                senderId: clientId.value
            });
        } catch (e) {
            console.debug('[RealTimePresentation] setOutputState remote-action error:', e);
        }
    }
    broadcastSyncState();
    if (!allowSendSlides.value) return;
    if (state === 'live') {
        // volta a projetar a página atual
        if (currentPageObject.value) sendMessageToViewer(currentPageObject.value);
    } else if (state === 'frozen') {
        // segura: mantém a última enviada, navegação só move o preview
        return;
    } else {
        publishOutputState();
    }
}

const handleUpdateEffects = (enabled) => {
    try {
        if (!docV2.value) docV2.value = normalizeToV2(null, { playlistId: route.params.id });
        docV2.value.configuration = { ...(docV2.value.configuration || {}), effects: enabled };
        effectsEnabled.value = enabled;
        presentationGroup.value.presentations.forEach((presentation) => {
            presentation.pages.forEach((page) => {
                page.effect = enabled;
            });
        });
        savePresentation();
    } catch (err) {
        console.error('[Presentation] handleUpdateEffects:', err);
    }
};

const handleUpdateEffectType = (type) => {
    try {
        if (!docV2.value) docV2.value = normalizeToV2(null, { playlistId: route.params.id });
        docV2.value.configuration = { ...(docV2.value.configuration || {}), effectType: type, transition: type };
        savePresentation();
    } catch (err) {
        console.error('[Presentation] handleUpdateEffectType:', err);
    }
};

const handleUpdateEffectDuration = (durationMs) => {
    try {
        if (!docV2.value) docV2.value = normalizeToV2(null, { playlistId: route.params.id });
        docV2.value.configuration = { ...(docV2.value.configuration || {}), effectDuration: durationMs, transitionDuration: durationMs };
        savePresentation();
    } catch (err) {
        console.error('[Presentation] handleUpdateEffectDuration:', err);
    }
};

const togglePageEffect = () => {
    if (!currentPageObject.value?.editable) return;
    currentPageObject.value.effect = currentPageObject.value.effect === false;
    syncViewFromState();
    savePresentation();
}

const createNewSong = () => {
    const newSong = {
        title: `New Song ${songsPresentation.value.length + 1}`,
        presentation: [{content: "", title: "", subtitle: "", header: "", background: "", effect: true, editable: false}]
    };
    
    songsPresentation.value.push(newSong);
    
    const presentation = new Presentation();
    presentation.songId = null;
    presentation.title = newSong.title;
    presentation.lines = [];
    presentation.groupPages(groupSize.value);
    presentationGroup.value.addPresentation(presentation);
    
    currentSongIndex.value = songsPresentation.value.length - 1;
    loadSong(currentSongIndex.value);
    
    savePresentation();
}

const removeSong = () => {
    if (confirm("Are you sure you want to remove this song?")) {
        songsPresentation.value.splice(currentSongIndex.value, 1);
        presentationGroup.value.presentations.splice(currentSongIndex.value, 1);
        currentSongIndex.value = 0;
        loadSong(currentSongIndex.value);
        savePresentation();
    }
}

const handleTitleUpdate = (newTitle) => {
    if (songsPresentation.value[currentSongIndex.value]) {
        songsPresentation.value[currentSongIndex.value].title = newTitle;
        presentationGroup.value.getPresentation(currentSongIndex.value).title = newTitle;
        savePresentation();
    }
}

const savePresentation = async () => {
    if (saving.value || !presentationLoaded.value) return;

    saving.value = true;
    try {
        // Persiste sempre no padrão v2 (com configuration/name/roomId preservados)
        docV2.value = groupToV2(presentationGroup.value, {
            previous: docV2.value,
            playlistId: route.params.id,
        });
        const response = await presentationService.savePresentation(docV2.value);
        if (response.ok) {
            toastStore.dispatch('addMessage', {
                message: 'Presentation saved',
                icon: 'success',
                ttl: 3000
            });
        }
        else {
            toastStore.dispatch('addMessage', {
                message: 'Error saving presentation',
                icon: 'error',
                ttl: 3000
            });
        }
    } catch (e) {
        console.error('Error saving presentation:', e);
        toastStore.dispatch('addMessage', {
            message: 'Error saving presentation',
            icon: 'error',
            ttl: 3000
        });
    } finally {
        saving.value = false;
    }
}

const uploadTarget = ref('layer');

const openLogoUpload = () => {
    uploadTarget.value = 'logo';
    if (imageUploadPanel.value) {
        imageUploadPanel.value.open();
    }
};

const openImageUploadPanel = () => {
    uploadTarget.value = 'layer';
    if (imageUploadPanel.value) {
        imageUploadPanel.value.open();
    }
}

const onImageUploadPanelClose = () => {
    uploadTarget.value = 'layer';
}

const removeLogo = () => {
    if (docV2.value?.configuration?.logoUrl) {
        delete docV2.value.configuration.logoUrl;
        savePresentation();
        if (outputState.value === 'logo') publishOutputState();
        toastStore.dispatch('addMessage', {
            message: t('presentation.removeLogo'),
            icon: 'info',
            ttl: 2000
        });
    }
};

const handleImageSelected = (file) => {
    if (file && file.url) {
        if (uploadTarget.value === 'logo') {
            if (!docV2.value) docV2.value = normalizeToV2(null, { playlistId: route.params.id });
            docV2.value.configuration = {
                ...(docV2.value.configuration || {}),
                logoUrl: file.url,
            };
            savePresentation();
            if (outputState.value === 'logo') publishOutputState();
            toastStore.dispatch('addMessage', {
                message: t('presentation.logoConfigured'),
                icon: 'success',
                ttl: 3000
            });
            if (imageUploadPanel.value) imageUploadPanel.value.close();
            return;
        }
        handleAddImageLayer(file.url);
        toastStore.dispatch('addMessage', {
            message: 'Image layer added',
            icon: 'success',
            ttl: 3000
        });
    }
}

const onSongBackgroundSelected = ({ type, value }) => {
    if (type === 'remove') {
        handleUpdateSongVideo(null);
        return;
    }
    if (!value) return;
    if (type === 'video') {
        handleAddVideoLayer(value);
    } else {
        handleAddImageLayer(value);
    }
};


const openPresentationView = () => {
    openPresentationWindow(roomId.value);
};

</script>
<style scoped>
.next-view, .current-view {
    background: black;
    color: white;
}

.empty-header {
    @apply border border-gray-300;
}
.animated-text {
  transition: opacity 0.5s ease-in-out;
}

.animated-text.fade-in {
  opacity: 1;
}

.animated-text.fade-out {
  opacity: 0;
}
</style>

